import { FC, useEffect } from "react";
import "./ListArticlesView.scss";
import { Link } from "react-router-dom";
import { BvIcon } from "bevi-icon";
import TooltipDefault from "../../TooltipDefault/TooltipDefault";

interface ListArticlesViewProps {
  articlesStructure?: any;
  articles?: any[];
  viewMode?: "category" | "section" | "subsection" | "articles";
  viewInfo?: boolean;
}

// "GOVERNO DE SÃO PAULO - X" (CEETEPS, DAEE, etc.) fica escondido daqui;
// só aparece ao entrar em "GOVERNO DE SÃO PAULO".
const SP_BASE_NAME = "GOVERNO DE SÃO PAULO";

const ListArticlesView: FC<ListArticlesViewProps> = ({
  articlesStructure,
  articles,
  viewMode,
  viewInfo = false,
}) => {
  const hasContent = (section: any) => {
    const hasInSection =
      Array.isArray(section?.section_articles) &&
      section.section_articles.length > 0;
    const hasInSubsections =
      Array.isArray(section?.subsections) &&
      section.subsections.some(
        (sub: any) =>
          Array.isArray(sub?.subsection_articles) &&
          sub.subsection_articles.length > 0,
      );
    return hasInSection || hasInSubsections;
  };

  const subsectionHasContent = (subsection: any) =>
    Array.isArray(subsection?.subsection_articles) &&
    subsection.subsection_articles.length > 0;

  return (
    <div className="ListArticlesView" data-testid="ListArticlesView">
      {viewMode === "articles" && (
        <>
          {articles && articles.length > 0 ? (
            <ul className="articles-wrapper d-grid my-4">
              {articles.map((article: any) => (
                <li key={article?.article?.id || article?.id}>
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <Link
                      to={`/articles/view/${article?.article?.id || article?.id}`}
                    >
                      {article?.article?.title ||
                        article?.title ||
                        String(article)}
                    </Link>
                    {viewInfo && (
                      <TooltipDefault
                        id={`tooltip-${article?.article?.id || article?.id}`}
                        text={article?.article_comment?.comment || ""}
                        position="top"
                        backgroundColor="#bcbddc"
                        color="#454787"
                      >
                        <BvIcon
                          name="info"
                          variant="duo"
                          width={20}
                          height={20}
                        />
                      </TooltipDefault>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted mt-3">Não há artigos disponíveis.</p>
          )}
        </>
      )}

      {articlesStructure?.category_articles &&
        articlesStructure.category_articles.length > 0 && (
          <div className="mb-5">
            <h3>{articlesStructure.category_name}</h3>
            <ul className="articles-wrapper my-4">
              {articlesStructure?.category_articles?.map((article: any) => (
                <li key={article?.articles_id}>
                  <Link to={`/articles/view/${article?.articles_id}`}>
                    {article?.articles_name ?? String(article)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

      {(() => {
        let sections = [];
        let subsections = [];
        if (
          viewMode === "category" &&
          Array.isArray(articlesStructure?.sections)
        ) {
          sections = articlesStructure.sections;
        }
        if (viewMode === "section" && Array.isArray(articlesStructure)) {
          sections = articlesStructure;
        }
        if (viewMode === "subsection" && Array.isArray(articlesStructure)) {
          subsections = articlesStructure;
        }

        if (viewMode === "category" || viewMode === "section") {
          const sectionsWithContent = sections.filter(hasContent);

          const isGovernosCategory = articlesStructure?.category_id === 3;
          const groupedVariantIds = new Set<number>();
          if (isGovernosCategory) {
            sectionsWithContent.forEach((other: any) => {
              if (other.section_name?.startsWith(`${SP_BASE_NAME} - `)) {
                groupedVariantIds.add(other.section_id);
              }
            });
          }

          const displaySections = groupedVariantIds.size
            ? sectionsWithContent.filter(
                (s: any) => !groupedVariantIds.has(s.section_id),
              )
            : sectionsWithContent;

          return displaySections?.map((section: any, idx: number) => (
            <div key={section.section_id ?? idx} className="mb-5">
              <div className="section-header">
                <h3>
                  <Link to={`/articles/sections/view/${section.section_id}`}>
                    {section.section_name}
                  </Link>
                </h3>
                {section?.section_description && (
                  <p className="section-description">
                    {section.section_description}
                  </p>
                )}
              </div>

              {articlesStructure?.category_id !== 3 && (
                <>
                  {Array.isArray(section?.subsections) &&
                    section.subsections.some(subsectionHasContent) && (
                      <div className="subsection-wrapper mb-4">
                        {section.subsections.map((sub: any) =>
                          subsectionHasContent(sub) ? (
                            <div key={sub.subsection_id}>
                              <div className="subsection-header">
                                <h5>{sub.subsection_name}</h5>
                                {sub?.subsection_description && (
                                  <p className="section-description">
                                    {sub.subsection_description}
                                  </p>
                                )}
                              </div>
                              <ul className="articles-wrapper my-4">
                                {sub.subsection_articles.map((article: any) => (
                                  <li key={article?.articles_id}>
                                    <Link
                                      to={`/articles/view/${article?.articles_id}`}
                                    >
                                      {article?.articles_name ??
                                        String(article)}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null,
                        )}
                      </div>
                    )}

                  {Array.isArray(section?.section_articles) &&
                    section.section_articles.length > 0 && (
                      <ul className="articles-wrapper my-4">
                        {section.section_articles.map((article: any) => (
                          <li key={article?.articles_id}>
                            <Link to={`/articles/view/${article?.articles_id}`}>
                              {article?.articles_name ?? String(article)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}

                  {idx < displaySections.length - 1 && <hr />}
                </>
              )}
            </div>
          ));
        } else {
          return subsections?.map((sub: any, idx: number) => (
            <div
              key={sub.subsection_id ?? idx}
              className="subsection-wrapper mb-4"
            >
              <div key={sub.subsection_id}>
                <div className="subsection-header">
                  <h5>{sub.subsection_name}</h5>
                  {sub?.subsection_description && (
                    <p className="section-description">
                      {sub.subsection_description}
                    </p>
                  )}
                </div>
                <ul className="articles-wrapper my-4">
                  {sub?.subsection_articles?.map((article: any) => (
                    <li key={article?.articles_id}>
                      <Link to={`/articles/view/${article?.articles_id}`}>
                        {article?.articles_name ?? String(article)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ));
        }
      })()}
    </div>
  );
};

export default ListArticlesView;
